import React from "react"
import moment from "moment"
import { Badge } from "reactstrap"
import { Link } from "react-router-dom"
import {
  isTenis10Event,
  tenis10CategoryLabel,
} from "pages/EventsManagement/tenis10Event"
import { childNumber } from "pages/Tenis10Claims/claimsLabels"

const matchesPlayer = (value, userId) =>
  value !== undefined &&
  value !== null &&
  userId !== undefined &&
  userId !== null &&
  String(value) === String(userId)

const registrationLists = [
  {
    color: "success",
    key: "mainList",
    label: "Main list",
  },
  {
    color: "warning",
    key: "waitingList",
    label: "Waiting list",
  },
  {
    color: "info",
    key: "preRegistrationList",
    label: "Pre-registration",
  },
]

const humanizeValue = value => {
  if (!value) return null

  const words = String(value)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLowerCase()

  return words.replace(/^./, character => character.toUpperCase())
}

// The participant a slot enters: a Tenis10 child by its profile (siblings share the parent's account), else the account itself.
const participantKeyOf = slot =>
  slot?.profileId ? `profile:${slot.profileId}` : "account"

// A2 (Tudor, 2026-10-29): the competition's rows for this account, one row per participant — the account (its own entry, or as
// a doubles partner) and each Tenis10 child. A participant's standing entry is shown before its own withdrawal.
const flattenCompetition = (row, competition, userId) => {
  if (userId === undefined || userId === null) return [row]
  const registrations = getPlayerRegistrations(competition, userId)
  if (!registrations.length) return [{ ...row, playerRegistration: null }]
  return registrations.map(playerRegistration => ({
    ...row,
    playerRegistration,
    rowKey: `${competition?._id}:${participantKeyOf(
      playerRegistration.registration
    )}`,
  }))
}

export const flattenTournamentEntries = (events, userId) =>
  (events?.results || []).flatMap(event =>
    (event?.competitions || []).flatMap(competition =>
      flattenCompetition(
        {
          ...competition,
          eventCategory: event?.category,
          eventEndDate: event?.endDate,
          eventId: event?._id,
          eventName: event?.name,
          eventProgramType: event?.programType,
          eventSportType: event?.sportType,
          eventStartDate: event?.startDate,
          rowKey: competition?._id,
        },
        competition,
        userId
      )
    )
  )

const getEntrySortTime = (entry, field, fallback) => {
  const date = moment(entry?.[field])
  return date.isValid() ? date.valueOf() : fallback
}

export const mergeTournamentEntries = (currentEntries, pastEntries, userId) =>
  [
    ...flattenTournamentEntries(currentEntries, userId).map(entry => ({
      ...entry,
      entryPeriod: "current",
    })),
    ...flattenTournamentEntries(pastEntries, userId).map(entry => ({
      ...entry,
      entryPeriod: "past",
    })),
  ].sort((firstEntry, secondEntry) => {
    if (firstEntry.entryPeriod !== secondEntry.entryPeriod) {
      return firstEntry.entryPeriod === "current" ? -1 : 1
    }

    if (firstEntry.entryPeriod === "current") {
      return (
        getEntrySortTime(firstEntry, "eventStartDate", Infinity) -
        getEntrySortTime(secondEntry, "eventStartDate", Infinity)
      )
    }

    return (
      getEntrySortTime(secondEntry, "eventEndDate", -Infinity) -
      getEntrySortTime(firstEntry, "eventEndDate", -Infinity)
    )
  })

export const getTournamentEntryRowClass = entry =>
  entry?.entryPeriod === "current" ? "table-success" : ""

const isPlayersSlot = (slot, userId) =>
  matchesPlayer(slot?.userId, userId) || matchesPlayer(slot?.partnerId, userId)

// The lists keep the account's own children's WITHDRAWN markers (Events getMyEvents, keepOwnChildMarker): an entry that
// still stands is shown before a withdrawal.
const isWithdrawn = slot => slot?.status === "WITHDRAWN"

export const getPlayerRegistration = (entry, userId) => {
  for (const standing of [true, false]) {
    for (const list of registrationLists) {
      const registration = (entry?.[list.key] || []).find(
        slot => isPlayersSlot(slot, userId) && isWithdrawn(slot) !== standing
      )

      if (registration) {
        return {
          ...list,
          isPartner: matchesPlayer(registration.partnerId, userId),
          registration,
        }
      }
    }
  }

  return null
}

export const getPlayerRegistrations = (entry, userId) => {
  const found = new Map()
  for (const standing of [true, false]) {
    for (const list of registrationLists) {
      for (const slot of entry?.[list.key] || []) {
        const key = participantKeyOf(slot)
        if (
          isPlayersSlot(slot, userId) &&
          isWithdrawn(slot) !== standing &&
          !found.has(key)
        ) {
          found.set(key, {
            ...list,
            isPartner: matchesPlayer(slot.partnerId, userId),
            registration: slot,
          })
        }
      }
    }
  }
  return [...found.values()]
}

// The registration a row shows: its participant's, or — for a row built without the account — the competition's first.
const registrationOf = (entry, userId) =>
  entry?.playerRegistration !== undefined
    ? entry.playerRegistration
    : getPlayerRegistration(entry, userId)

// The table names each row's participant (the Participant column) only when it holds a Tenis10 child's entry: an account
// without children keeps its five columns.
export const hasChildEntries = entries =>
  (entries || []).some(
    entry => entry?.playerRegistration?.registration?.profileId
  )

// The events of a joined/closed read, as the account's own and its children's (Tenis10, `profileId`): an event where only the
// children play is not the account's own, an event with both counts in both.
export const countPlayerEvents = (events, userId) =>
  (events?.results || []).reduce(
    (counts, event) => {
      const slots = (event?.competitions || [])
        .flatMap(competition =>
          registrationLists.flatMap(list => competition?.[list.key] || [])
        )
        .filter(slot => isPlayersSlot(slot, userId) && !isWithdrawn(slot))
      const forChildren = slots.some(slot => slot.profileId)
      const childrenOnly = forChildren && slots.every(slot => slot.profileId)

      return {
        own: counts.own + (childrenOnly ? 0 : 1),
        children: counts.children + (forChildren ? 1 : 0),
      }
    },
    { own: 0, children: 0 }
  )

export const formatTournamentDateRange = (startDate, endDate) => {
  const start = moment(startDate)
  const end = moment(endDate)

  if (!start.isValid() && !end.isValid()) return "Dates not recorded"
  if (!start.isValid()) return end.format("D MMM YYYY")
  if (!end.isValid()) return start.format("D MMM YYYY")

  if (start.year() === end.year()) {
    return `${start.format("D MMM")} – ${end.format("D MMM YYYY")}`
  }

  return `${start.format("D MMM YYYY")} – ${end.format("D MMM YYYY")}`
}

const getTournamentStatus = endDate => {
  const end = moment(endDate)

  if (!end.isValid()) {
    return { color: "secondary", label: "Status unknown" }
  }

  return end.isBefore(moment())
    ? { color: "secondary", label: "Past" }
    : { color: "success", label: "Active or upcoming" }
}

const formatTournament = (cellContent, entry) => (
  <div style={{ minWidth: "180px", whiteSpace: "normal" }}>
    <Link
      className="font-weight-semibold text-dark"
      target="_blank"
      to={`/events/${entry?.eventId}/details`}
    >
      {entry?.eventName || "Unnamed tournament"}
    </Link>
    <div className="mt-1 d-flex flex-wrap">
      {/* a Tenis10 event is named by its programme, as its details page does, not by its sport (tennis) */}
      {isTenis10Event({ programType: entry?.eventProgramType }) ? (
        <span className="badge badge-soft-primary mr-1 mb-1">Tenis10</span>
      ) : entry?.eventSportType && (
        <span className="badge badge-soft-primary text-capitalize mr-1 mb-1">
          {entry.eventSportType}
        </span>
      )}
      {entry?.eventCategory && (
        <span className="badge badge-soft-secondary text-capitalize mr-1 mb-1">
          {entry.eventCategory}
        </span>
      )}
    </div>
  </div>
)

const formatSchedule = (cellContent, entry) => {
  const status = getTournamentStatus(entry?.eventEndDate)

  return (
    <div style={{ minWidth: "145px" }}>
      <div className="font-weight-semibold">
        {formatTournamentDateRange(
          entry?.eventStartDate,
          entry?.eventEndDate
        )}
      </div>
      <Badge color={status.color} pill className="mt-1 px-2 py-1">
        {status.label}
      </Badge>
    </div>
  )
}

// A Tenis10 competition is named by its Tenis10 category, „Verde · U10”: the child's colour when it entered (a child enters
// only its own colour, common/tenis10Eligibility), never the adult level the schema also gives it (PRD 16 LR11).
const competitionLevelLabel = entry =>
  entry?.tenis10Level
    ? tenis10CategoryLabel(entry)
    : entry?.level
    ? `Level ${entry.level}`
    : null

const formatCompetition = (cellContent, entry) => (
  <div style={{ minWidth: "140px", whiteSpace: "normal" }}>
    <Link
      className="font-weight-semibold"
      target="_blank"
      to={`/events/${entry?.eventId}/competitions/${entry?._id}/entries`}
    >
      Competition #{entry?.competitionId || "—"}
    </Link>
    <div className="text-muted font-size-12 mt-1 text-capitalize">
      {[entry?.gameType, competitionLevelLabel(entry)]
        .filter(Boolean)
        .join(" · ") || "Format not recorded"}
    </div>
  </div>
)

// Who the row's entry is for: a Tenis10 child by its name and its own number (siblings share this account's number), else
// this account.
const formatParticipant = (cellContent, entry, userId) => {
  const registration = registrationOf(entry, userId)?.registration
  const isChild = Boolean(registration?.profileId)
  const name = isChild
    ? [registration.profile?.firstName, registration.profile?.lastName]
        .filter(Boolean)
        .join(" ") || "Tenis10 child"
    : "This player"
  const number = isChild
    ? childNumber(registration.profile) ?? childNumber(registration)
    : null
  const detail = isChild
    ? number != null && `#${number}`
    : [userId != null && `#${userId}`, "this account"]
        .filter(Boolean)
        .join(" · ")

  return (
    <div style={{ minWidth: "150px", whiteSpace: "normal" }}>
      <div className="font-weight-semibold">{name}</div>
      {detail && (
        <div className="text-muted font-size-12 mt-1">{detail}</div>
      )}
      {isChild && (
        <span className="badge badge-soft-info font-size-11 mt-1">
          Tenis10 child
        </span>
      )}
    </div>
  )
}

const formatRegistration = (cellContent, entry, userId) => {
  const playerRegistration = registrationOf(entry, userId)

  if (!playerRegistration) {
    return <span className="text-muted">Registration details unavailable</span>
  }

  const { color, isPartner, label, registration } = playerRegistration
  const entryTimestamp = registration?.createdAt || registration?.registeredAt
  const entryDate = entryTimestamp ? moment(entryTimestamp) : null
  const registrationStatus = humanizeValue(registration?.status)
  const registrationStatusColor =
    String(registration?.status || "").toLowerCase() === "confirmed"
      ? "success"
      : String(registration?.status || "").toLowerCase() === "pending"
      ? "warning"
      : "secondary"

  return (
    <div style={{ minWidth: "190px", whiteSpace: "normal" }}>
      <div className="d-flex align-items-center flex-nowrap">
        <Badge color={color} pill className="px-2 py-1 mr-1 flex-shrink-0">
          {label}
        </Badge>
        <Badge
          color={registrationStatusColor}
          pill
          className="px-2 py-1 font-size-11 flex-shrink-0"
        >
          {registrationStatus || "Status not recorded"}
        </Badge>
      </div>
      {isPartner && (
        <div className="font-size-11 mt-1">Registered as partner</div>
      )}
      <div className="text-muted font-size-11 mt-1">
        {entryDate?.isValid()
          ? `Entered ${entryDate.format("D MMM YYYY, HH:mm")}`
          : "Entry date not recorded"}
      </div>
    </div>
  )
}

const formatFee = (cellContent, entry, userId) => {
  const playerRegistration = registrationOf(entry, userId)
  const registration = playerRegistration?.registration

  if (registration?.amount === undefined || registration?.amount === null) {
    return <span className="text-muted">Not recorded</span>
  }

  const isDoubles =
    entry?.gameType === "doubles" || Boolean(registration?.partnerId)
  // A Tenis10 child's entry rides on this (the parent's) account with the child's profileId: the parent pays for it, on site
  // at the club when it is an entry paid on site (`override`, confirmed unpaid by Payments, PRD 09 O3)
  const isChildEntry = Boolean(registration?.profileId)
  const paidOnSite = isChildEntry && registration?.override === true
  const paid = registration?.paid
  const paymentStatus =
    paid === true
      ? { color: "success", label: "Paid" }
      : paidOnSite
      ? { color: "info", label: "Paid on site" }
      : paid === false
      ? { color: "danger", label: "Not marked paid" }
      : { color: "secondary", label: "Collection unknown" }
  const primaryEntrantId = registration?.userId
  const payerDescription = isChildEntry
    ? paid === true
      ? "Paid by the parent"
      : paidOnSite
      ? "The parent pays the club on site"
      : "Collection assigned to the parent"
    : playerRegistration?.isPartner
    ? paid === true
      ? `Primary entrant #${primaryEntrantId || "—"} paid`
      : `Collection assigned to primary entrant #${primaryEntrantId || "—"}`
    : paid === true
    ? isDoubles
      ? "This player paid for the team"
      : "Paid by this player"
    : isDoubles
    ? "Collection assigned to this player for the team"
    : "Collection assigned to this player"

  return (
    <div style={{ minWidth: "185px", whiteSpace: "normal" }}>
      <div className="d-flex align-items-center flex-nowrap">
        <span className="font-weight-semibold mr-2 text-nowrap">
          {registration.amount} {registration.currency || ""}
        </span>
        <Badge
          color={paymentStatus.color}
          pill
          className="px-2 py-1 font-size-11 flex-shrink-0"
        >
          {paymentStatus.label}
        </Badge>
      </div>
      <div className="text-muted font-size-11 mt-1">
        {isChildEntry
          ? "Child's entry"
          : isDoubles
          ? "Team total · 2 players"
          : "Player total"}
      </div>
      <div className="font-size-11 mt-1">{payerDescription}</div>
    </div>
  )
}

// `participants`: the Participant column, second, for a table that holds a Tenis10 child's entry (`hasChildEntries`).
const tournamentEntryColumns = (userId, { participants = false } = {}) => [
  {
    dataField: "eventName",
    text: "Tournament",
    formatter: formatTournament,
    headerStyle: { width: participants ? "20%" : "24%" },
  },
  ...(participants
    ? [
        {
          dataField: "participant",
          text: "Participant",
          formatter: (cellContent, entry) =>
            formatParticipant(cellContent, entry, userId),
          headerStyle: { width: "16%" },
        },
      ]
    : []),
  {
    dataField: "eventStartDate",
    text: "Schedule",
    formatter: formatSchedule,
    headerStyle: { width: participants ? "14%" : "17%" },
  },
  {
    dataField: "competitionId",
    text: "Competition",
    formatter: formatCompetition,
    headerStyle: { width: participants ? "14%" : "18%" },
  },
  {
    dataField: "registration",
    text: "Registration",
    formatter: (cellContent, entry) =>
      formatRegistration(cellContent, entry, userId),
    headerStyle: { width: participants ? "18%" : "21%" },
  },
  {
    dataField: "amount",
    text: "Payment",
    formatter: (cellContent, entry) => formatFee(cellContent, entry, userId),
    headerStyle: { width: participants ? "18%" : "20%" },
  },
]

export default tournamentEntryColumns
